// ABOUTME: Centralized notification service for in-app and push notifications
// ABOUTME: Manages notification history, preferences, and delivery

import { toast, type ExternalToast } from 'sonner'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Notification types supported by the app
 */
export type NotificationType =
  | 'achievement'
  | 'level_up'
  | 'challenge_complete'
  | 'streak'
  | 'quiz_complete'
  | 'new_content'
  | 'reminder'
  | 'system'

/**
 * Notification priority levels
 */
export type NotificationPriority = 'low' | 'medium' | 'high'

/**
 * Notification data structure
 */
export interface AppNotification {
  id: string
  type: NotificationType
  title: string
  message: string
  priority: NotificationPriority
  read: boolean
  createdAt: Date
  expiresAt?: Date
  action?: {
    label: string
    path: string
  }
  metadata?: Record<string, unknown>
}

/**
 * Notification preferences
 */
export interface NotificationPreferences {
  enabled: boolean
  pushEnabled: boolean
  inAppEnabled: boolean
  quietHoursEnabled: boolean
  quietHoursStart: string // HH:MM format
  quietHoursEnd: string
  types: Record<NotificationType, boolean>
}

/**
 * Notification store state
 */
interface NotificationStore {
  notifications: AppNotification[]
  preferences: NotificationPreferences
  addNotification: (notification: Omit<AppNotification, 'id' | 'createdAt' | 'read'>) => string
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  removeNotification: (id: string) => void
  clearAll: () => void
  clearExpired: () => void
  updatePreferences: (prefs: Partial<NotificationPreferences>) => void
  getUnreadCount: () => number
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  enabled: true,
  pushEnabled: false,
  inAppEnabled: true,
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '08:00',
  types: {
    achievement: true,
    level_up: true,
    challenge_complete: true,
    streak: true,
    quiz_complete: true,
    new_content: true,
    reminder: true,
    system: true,
  },
}

/**
 * Zustand store for notification management
 */
export const useNotificationStore = create<NotificationStore>()(
  persist(
    (set, get) => ({
      notifications: [],
      preferences: DEFAULT_PREFERENCES,

      addNotification: (notification) => {
        const id = `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        const newNotification: AppNotification = {
          ...notification,
          id,
          createdAt: new Date(),
          read: false,
        }

        set((state) => ({
          notifications: [newNotification, ...state.notifications].slice(0, 100), // Keep max 100
        }))

        return id
      },

      markAsRead: (id) => {
        set((state) => ({
          notifications: state.notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
        }))
      },

      markAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
        }))
      },

      removeNotification: (id) => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }))
      },

      clearAll: () => {
        set({ notifications: [] })
      },

      clearExpired: () => {
        const now = new Date()
        set((state) => ({
          notifications: state.notifications.filter((n) => !n.expiresAt || new Date(n.expiresAt) > now),
        }))
      },

      updatePreferences: (prefs) => {
        set((state) => ({
          preferences: { ...state.preferences, ...prefs },
        }))
      },

      getUnreadCount: () => {
        return get().notifications.filter((n) => !n.read).length
      },
    }),
    {
      name: 'gentleman-notifications',
      partialize: (state) => ({
        notifications: state.notifications,
        preferences: state.preferences,
      }),
    }
  )
)

/**
 * Notification service for sending and managing notifications
 */
export class NotificationService {
  /**
   * Check if notifications are in quiet hours
   */
  private isInQuietHours(): boolean {
    const { preferences } = useNotificationStore.getState()

    if (!preferences.quietHoursEnabled) {
      return false
    }

    const now = new Date()
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`

    const { quietHoursStart, quietHoursEnd } = preferences

    // Handle overnight quiet hours (e.g., 22:00 - 08:00)
    if (quietHoursStart > quietHoursEnd) {
      return currentTime >= quietHoursStart || currentTime < quietHoursEnd
    }

    return currentTime >= quietHoursStart && currentTime < quietHoursEnd
  }

  /**
   * Check if notification type is enabled
   */
  private isTypeEnabled(type: NotificationType): boolean {
    const { preferences } = useNotificationStore.getState()
    return preferences.enabled && preferences.types[type]
  }

  /**
   * Get toast options based on notification type
   */
  private getToastOptions(type: NotificationType, priority: NotificationPriority): ExternalToast {
    const baseDuration = priority === 'high' ? 8000 : priority === 'medium' ? 5000 : 3000

    const typeIcons: Record<NotificationType, string> = {
      achievement: '🏆',
      level_up: '⬆️',
      challenge_complete: '✅',
      streak: '🔥',
      quiz_complete: '📝',
      new_content: '🆕',
      reminder: '⏰',
      system: 'ℹ️',
    }

    return {
      duration: baseDuration,
      icon: typeIcons[type],
    }
  }

  /**
   * Send a notification
   */
  notify(options: {
    type: NotificationType
    title: string
    message: string
    priority?: NotificationPriority
    action?: AppNotification['action']
    metadata?: Record<string, unknown>
    expiresAt?: Date
    showToast?: boolean
  }): string | null {
    const { type, title, message, priority = 'medium', action, metadata, expiresAt, showToast = true } = options

    // Check if type is enabled
    if (!this.isTypeEnabled(type)) {
      return null
    }

    // Store notification
    const store = useNotificationStore.getState()
    const id = store.addNotification({
      type,
      title,
      message,
      priority,
      action,
      metadata,
      expiresAt,
    })

    // Show toast if enabled and not in quiet hours
    const { preferences } = store
    if (showToast && preferences.inAppEnabled && !this.isInQuietHours()) {
      const toastOptions = this.getToastOptions(type, priority)

      // Use appropriate toast method based on priority
      if (priority === 'high') {
        toast.warning(title, {
          description: message,
          ...toastOptions,
        })
      } else {
        toast.success(title, {
          description: message,
          ...toastOptions,
        })
      }
    }

    return id
  }

  /**
   * Achievement unlocked notification
   */
  notifyAchievement(title: string, description: string, rarity: 'common' | 'rare' | 'epic' | 'legendary'): string | null {
    const rarityEmoji: Record<string, string> = {
      common: '⭐',
      rare: '💫',
      epic: '✨',
      legendary: '🏆',
    }

    return this.notify({
      type: 'achievement',
      title: `${rarityEmoji[rarity]} Achievement Unlocked!`,
      message: `${title} - ${description}`,
      priority: rarity === 'legendary' ? 'high' : rarity === 'epic' ? 'medium' : 'low',
      metadata: { rarity },
    })
  }

  /**
   * Level up notification
   */
  notifyLevelUp(newLevel: number, levelTitle: string): string | null {
    return this.notify({
      type: 'level_up',
      title: `Level ${newLevel} Reached!`,
      message: `Congratulations! You've become a ${levelTitle}`,
      priority: 'high',
      metadata: { level: newLevel, levelTitle },
    })
  }

  /**
   * Challenge complete notification
   */
  notifyChallenge(challengeName: string, xpEarned: number): string | null {
    return this.notify({
      type: 'challenge_complete',
      title: 'Challenge Completed!',
      message: `${challengeName} - +${xpEarned} XP`,
      priority: 'medium',
      metadata: { challengeName, xpEarned },
    })
  }

  /**
   * Streak notification
   */
  notifyStreak(streakDays: number, isAboutToBreak = false): string | null {
    if (isAboutToBreak) {
      return this.notify({
        type: 'streak',
        title: 'Streak in Danger!',
        message: `Your ${streakDays} day streak is about to end. Complete a challenge to keep it going!`,
        priority: 'high',
        action: {
          label: 'Go to Challenges',
          path: '/challenges',
        },
      })
    }

    return this.notify({
      type: 'streak',
      title: `${streakDays} Day Streak!`,
      message: `You're on fire! Keep the momentum going.`,
      priority: streakDays >= 7 ? 'high' : 'medium',
      metadata: { streakDays },
    })
  }

  /**
   * Quiz complete notification
   */
  notifyQuizComplete(moduleName: string, score: number, isPerfect: boolean): string | null {
    return this.notify({
      type: 'quiz_complete',
      title: isPerfect ? 'Perfect Score!' : 'Quiz Completed!',
      message: `${moduleName} - Score: ${score}%`,
      priority: isPerfect ? 'high' : 'medium',
      metadata: { moduleName, score, isPerfect },
    })
  }

  /**
   * Daily reminder notification
   */
  notifyReminder(message: string): string | null {
    return this.notify({
      type: 'reminder',
      title: 'Daily Reminder',
      message,
      priority: 'low',
    })
  }

  /**
   * System notification
   */
  notifySystem(title: string, message: string, priority: NotificationPriority = 'medium'): string | null {
    return this.notify({
      type: 'system',
      title,
      message,
      priority,
    })
  }

  /**
   * Mark notification as read
   */
  markAsRead(id: string): void {
    useNotificationStore.getState().markAsRead(id)
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): void {
    useNotificationStore.getState().markAllAsRead()
  }

  /**
   * Get unread notification count
   */
  getUnreadCount(): number {
    return useNotificationStore.getState().getUnreadCount()
  }

  /**
   * Get all notifications
   */
  getNotifications(): AppNotification[] {
    return useNotificationStore.getState().notifications
  }

  /**
   * Clear expired notifications
   */
  clearExpired(): void {
    useNotificationStore.getState().clearExpired()
  }

  /**
   * Update notification preferences
   */
  updatePreferences(prefs: Partial<NotificationPreferences>): void {
    useNotificationStore.getState().updatePreferences(prefs)
  }

  /**
   * Get notification preferences
   */
  getPreferences(): NotificationPreferences {
    return useNotificationStore.getState().preferences
  }
}

// Singleton instance
let serviceInstance: NotificationService | null = null

/**
 * Get notification service instance
 */
export function getNotificationService(): NotificationService {
  if (!serviceInstance) {
    serviceInstance = new NotificationService()
  }
  return serviceInstance
}

/**
 * Initialize notification system
 */
export function setupNotificationSystem(): void {
  const service = getNotificationService()

  // Clear expired notifications on startup
  service.clearExpired()

  console.log('Notification system initialized')
}
